import React from "react";
import { Modal, Form, Button } from "react-bootstrap";
import NotificationPreference from "features/notifications/notificationPreference";
import useUniqueId from "common/ui/useUniqueId";
import "./ProducerNotificationPreferences.scss";
import SetProducerAdditionalContactDetails from "./SetProducerAdditionalContactDetails";
import { Producer } from "./producer";
import IAppStore from "app/IAppStore";
import { useSelector } from "react-redux";

type NotificationRule = {
    isEmailEnabled: boolean;
    isSmsEnabled: boolean;
}

type PreferencePropsWithRule<TRule> = {
    rule: TRule;
    onRuleChange(rule: TRule, isReEnabled: boolean): void;
    children: any;
    isEmailUnsubscribedByUser: boolean;
    isEmailDisabled: boolean;
    isSmsDisabled: boolean;
    hideEmailSwitch?: boolean;
}

const ProducerNotificationPreference: React.FC<PreferencePropsWithRule<NotificationRule>> = (props) => {
    const id = useUniqueId();

    return <div className="notification-preference">
                <div className="row">
                    <div className="col rule-name">{props.children}</div>
                    <div className="col rule-col">
                    {!props.hideEmailSwitch && <><div className="row">
                        <Form.Check type="switch" label="" 
                            id={`email-${id}`}
                            inline
                            checked={props.rule.isEmailEnabled && !props.isEmailDisabled} 
                            disabled={props.isEmailDisabled}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                const isReEnabled = ev.target.checked && props.isEmailUnsubscribedByUser
                                props.onRuleChange({...props.rule, isEmailEnabled: ev.target.checked}, isReEnabled)
                            }} />
                        </div>
                        {props.isEmailUnsubscribedByUser && <div className="row badge badge-warning">Unsubscribed</div>}
                    </>}
                    </div>
                    <div className="col rule-col">
                        <div className="row">
                            <Form.Check type="switch" label="" 
                                id={`sms-${id}`}
                                inline
                                checked={props.rule.isSmsEnabled && !props.isSmsDisabled} 
                                disabled={props.isSmsDisabled}
                                onChange={(ev: React.ChangeEvent<HTMLInputElement>) => 
                                    props.onRuleChange({...props.rule, isSmsEnabled: ev.target.checked}, false)
                                } />
                        </div>
                    </div>
                </div>
            </div>
}

interface PreferenceProps {
    pref: NotificationPreference;
    onRuleChange(rule: any, isReEnabled: boolean): void;
    isEmailDisabled: boolean;
    isSmsDisabled: boolean;
}

const Preference: React.FC<PreferenceProps> = (props) => {
    
    switch (props.pref.notificationType) {
        case "OfferFilled":
            return <ProducerNotificationPreference 
                        isEmailDisabled={props.isEmailDisabled}
                        isSmsDisabled={props.isSmsDisabled}
                        isEmailUnsubscribedByUser={props.pref.isEmailUnsubscribedByUser}
                        rule={props.pref.rule} 
                        onRuleChange={props.onRuleChange}>
                        Filled Offers
                    </ProducerNotificationPreference>
        case "OfferExpired":
            return <ProducerNotificationPreference 
                        isEmailDisabled={props.isEmailDisabled} 
                        isSmsDisabled={props.isSmsDisabled}
                        isEmailUnsubscribedByUser={props.pref.isEmailUnsubscribedByUser} 
                        rule={props.pref.rule}
                        onRuleChange={props.onRuleChange}>
                    Expired Offers
                </ProducerNotificationPreference>
        case "OfferExpiring":
            return <ProducerNotificationPreference 
                        isEmailDisabled={props.isEmailDisabled} 
                        isSmsDisabled={props.isSmsDisabled}
                        isEmailUnsubscribedByUser={props.pref.isEmailUnsubscribedByUser} 
                        rule={props.pref.rule}
                        onRuleChange={props.onRuleChange}>
                    Expiring Offers
                </ProducerNotificationPreference>
        case "OfferCreated":
            return <ProducerNotificationPreference
                    isEmailDisabled={props.isEmailDisabled}
                    isSmsDisabled={props.isSmsDisabled}
                    isEmailUnsubscribedByUser={props.pref.isEmailUnsubscribedByUser}
                    rule={props.pref.rule}
                    onRuleChange={props.onRuleChange}>
                    Created Offers
                </ProducerNotificationPreference>
        case "CustomCommunication":
            return <ProducerNotificationPreference
                    isEmailDisabled
                    hideEmailSwitch
                    isSmsDisabled={props.isSmsDisabled}
                    isEmailUnsubscribedByUser={props.pref.isEmailUnsubscribedByUser}
                    rule={props.pref.rule}
                    onRuleChange={props.onRuleChange}>
                    Trader Communications
                </ProducerNotificationPreference>
        case "OfferUpdated":
            return <ProducerNotificationPreference
                    isEmailDisabled={props.isEmailDisabled}
                    isSmsDisabled={props.isSmsDisabled}
                    isEmailUnsubscribedByUser={props.pref.isEmailUnsubscribedByUser}
                    rule={props.pref.rule}
                    onRuleChange={props.onRuleChange}>
                    Offer Updated
                </ProducerNotificationPreference>
        default:
            return <></>
    }
}
  
export type ProducerNotificationModalProps = {
    preferences: NotificationPreference[];
    isEmailNotSet: boolean;
    isPhoneNotSet: boolean;
    onRuleChange(preference: NotificationPreference, isReEnabled: boolean): void;
    handleClose: () => void;
    producer: Producer;
};

const ProducerNotificationModal: React.FC<ProducerNotificationModalProps> = (props: ProducerNotificationModalProps) => {
   
    const isCustomer = useSelector((m: IAppStore) => m.userState.isCustomer);
    
    return (
        <Modal show onHide={() => props.handleClose()} backdrop="static" >
            <Modal.Header closeButton>
                <Modal.Title>
                    {isCustomer ? "Settings" : "Notifications"}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>     
                <SetProducerAdditionalContactDetails producer={props.producer}/>       
                               
                <div className="producer-notification-preferences">
                    <div className="row header-row">
                        <div className="col"></div>
                        <div className="col">
                            <span className="header-name">Email </span>
                        </div>
                        <div className="col">
                            <span className="header-name">Text </span>
                        </div>
                    </div>
                    {props.preferences.map(m => 
                        <Preference 
                            isEmailDisabled={props.isEmailNotSet}
                            isSmsDisabled={props.isPhoneNotSet}
                            key={m.notificationType}
                            pref={m} 
                            onRuleChange={(rule, isReEnabled) => props.onRuleChange({...m, rule: rule}, isReEnabled)} 
                        />)}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="primary" onClick={() => props.handleClose()}>
                    Close
                </Button>
            </Modal.Footer>
            
        </Modal>
        
    );
}

export default ProducerNotificationModal;